import { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { VStack, Text } from '@chakra-ui/react';
import { NovaIcon } from 'components/ui/novaIcon';
import { NovaIconButton } from 'components/ui/NovaIconButton';
import { Table } from 'components/common/Table/Table';
import {
  useUsers,
  useKickUser,
  useChangeUserRole,
  useSchoolVouchers,
} from 'hooks/api/backend/managament/getB2BUsers';
import { useModalService } from 'hooks/useModalService';
import { ModalVariant } from 'types/Modal';
import { Pagination } from 'components/common/Table/Pagination';
import { Filters } from 'components/common/Table/Filters';
import { Column, UserData } from 'components/common/Table/types/Column';
import { OrganizationManagementLayout } from 'components/OrganizationManagement/OrganizationManagementLayout';
import { formattedDateWithMonthName } from 'utils/formattedDate';
import { VoucherOption } from 'components/common/Table/FilterOptions';
import { ClassListCell, TextCell } from 'components/common/Table/Cells';
import { EducationCell } from 'components/common/Table/Cells/EducationCell';
import { useNovaToast } from 'hooks/utils/useNovaToast';
import { toastContent } from 'config/toast';
import { ManagementStatusType } from 'types/StatusTypes';
import { TextCellProps } from 'components/common/Table/types/Cell';
import { UserDashboardVoucherCell } from 'components/common/Table/Cells/VoucherCell';
import { DateCell } from 'components/common/Table/Cells/DateCell';
import { InputOption } from 'components/common/Table/FilterOptions/InputOption';
import { createColumns } from 'utils/tableHelper';
import { SchoolUserRole } from '@novaheal/types';
import { RoleCell } from 'components/common/Table/Cells/RoleCell';
import { DeleteIcon, SearchIcon } from 'assets/icons/v2';
import storage from 'utils/storage';
import { QueryErrorState } from 'components/common/QueryErrorState';

const getUsersColumns = (
  handleRoleChange: (userId: string, newRole: SchoolUserRole) => void
): Column<UserData>[] =>
  createColumns<UserData>()({
    firstName: {
      header: 'Vorname',
      element: (props: TextCellProps) => <TextCell w="120px" label={props.label} lineClamp={3} />,
    },
    lastName: {
      header: 'Nachname',
      element: (props: TextCellProps) => <TextCell w="140px" label={props.label} lineClamp={3} />,
    },
    email: {
      header: 'E-Mail',
      element: (props: TextCellProps) => (
        <TextCell
          w="240px"
          label={props.label}
          textStyle="body.s.medium"
          lineClamp={3}
          textColor="text.tertiary"
        />
      ),
    },
    memberOfClasses: {
      header: 'Kurse',
      element: ClassListCell,
      getCellProps: (row: UserData) => ({
        classes: row.memberOfClasses || [],
      }),
      sortDisabled: true,
    },
    schoolRoles: {
      header: 'Rolle',
      element: RoleCell,
      getCellProps: (row: UserData) => {
        const isAdmin =
          row.schoolRoles?.some((role) => role.role === SchoolUserRole.ADMIN) || false;
        if (isAdmin) {
          return {
            label: 'Admin',
            value: SchoolUserRole.ADMIN,
            isDisabled: true,
            options: [{ value: SchoolUserRole.ADMIN, label: 'Admin' }],
          };
        }

        return {
          label: row.schoolRoles?.[0]?.role || SchoolUserRole.STUDENT,
          value: row.schoolRoles?.[0]?.role || SchoolUserRole.STUDENT,
          onChange: (newRole: string) => {
            handleRoleChange(row.id, newRole as SchoolUserRole);
          },
          options: [
            { value: SchoolUserRole.TEACHER, label: 'Lehrperson' },
            { value: SchoolUserRole.STUDENT, label: 'Lernende:r' },
            { value: SchoolUserRole.GUIDE, label: 'Praxisanleitung' },
          ],
        };
      },
      sortDisabled: true,
    },
    educationStatus: {
      header: 'Ausbildungsstand',
      element: EducationCell,
      getCellProps: (row: UserData) => ({
        label: row.educationStatus,
      }),
    },
    voucherShortCode: {
      header: 'Lizenz-ID',
      element: UserDashboardVoucherCell,
    },
    subscriptionUpdatedAt: {
      header: 'Aktivierungsdatum',
      element: DateCell,
    },
    actionItem: {
      header: 'Aktion',
      sortDisabled: true,
    },
  });

export const UserDashboard = (): ReactElement => {
  const modalService = useModalService();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const toast = useNovaToast();

  const { data: allVouchersData } = useSchoolVouchers();

  const selectedSchool = storage.getTenant();
  const [selectedVoucher, setSelectedVoucher] = useState<string>(searchParams.get('vId') || '');
  const [searchQuery, setsearchQuery] = useState<string>(decodeURI(searchParams.get('s') ?? ''));
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [sortBy, setSortBy] = useState<string | null>(searchParams.get('sby'));
  const [sortOrder, setSortOrder] = useState<string | null>(searchParams.get('sdir'));

  const searchTimeoutRef = useRef<NodeJS.Timeout>();

  const {
    data: usersData,
    refetch,
    isLoading,
    isError,
    error,
  } = useUsers({
    voucherId: selectedVoucher,
    page: currentPage,
    searchQuery,
    sortBy: sortBy ?? undefined,
    sortOrder: sortOrder ?? undefined,
  });
  const kickUserMutation = useKickUser(refetch);
  const changeUserRoleMutation = useChangeUserRole(refetch);

  const usedBy = useMemo(() => {
    if (!selectedSchool || !allVouchersData) {
      return 0;
    }
    if (selectedVoucher) {
      return allVouchersData.data.find((v) => v.id === selectedVoucher)?.timesUsed || 0;
    }
    const totalUsed = allVouchersData.data.reduce((sum, voucher) => sum + voucher.timesUsed, 0);
    return totalUsed;
  }, [selectedSchool, allVouchersData, selectedVoucher]);

  const navigateFilters = useCallback(
    (
      vId?: string | null,
      query?: string | null,
      sortBy?: string | null,
      sortOrder?: string | null
    ): void => {
      const encodedQuery = query ? encodeURIComponent(query) : undefined;
      const newUrl = `?${vId ? `&vId=${vId}` : ''}${query ? `&s=${encodedQuery}` : ''}${sortBy ? `&sby=${sortBy}` : ''}${sortOrder ? `&sdir=${sortOrder}` : ''}`;
      navigate(newUrl);
    },
    [navigate]
  );

  useEffect(() => {
    if (selectedSchool) {
      const vId = searchParams.get('vId');
      const query = searchParams.get('s');
      const sortBy = searchParams.get('sby');
      const sortOrder = searchParams.get('sdir');

      if (vId && allVouchersData?.data.some((v) => v.id === vId)) {
        setSelectedVoucher(vId);
      }

      setsearchQuery(query ?? '');
      setSortBy(sortBy);
      setSortOrder(sortOrder);

      navigateFilters(vId, query, sortBy, sortOrder);
    }
  }, [selectedSchool, searchParams, allVouchersData, navigateFilters]);

  const clearSort = (): void => {
    setSortBy(null);
    setSortOrder(null);
  };

  useEffect(() => {
    if (selectedSchool) {
      refetch();
    }
  }, [selectedSchool, selectedVoucher, refetch]);

  const handleVoucherChange = (voucherId: string): void => {
    clearSort();
    setSelectedVoucher(voucherId);
    navigateFilters(voucherId);
    resetPagination();
  };

  const handleSearchQueryChange = (query: string): void => {
    setsearchQuery(query);
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      navigateFilters(selectedVoucher, query);
      resetPagination();
      clearSort();
    }, 300);
  };

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const handleKickUser = (userId: string, voucherId: string): void => {
    modalService.show(ModalVariant.CONFIRMATION, {
      shouldGoBack: false,
      onConfirm: () => kickUserMutation.mutate({ userId, voucherId }),
      target: undefined,
    });
  };

  const resetPagination = (): void => {
    setCurrentPage(1);
  };

  const handlePageChange = (newPage: number): void => {
    setCurrentPage(newPage);
  };

  const handleRoleChange = async (userId: string, newRole: SchoolUserRole): Promise<void> => {
    const user = usersData?.data?.find((u) => u.id === userId);
    if (!user) return;
    const isMember = user.memberOfClasses && user.memberOfClasses.length > 0;
    const isTeacher = user.teacherOfClasses && user.teacherOfClasses.length > 0;
    const isGuide = user.guideOfClasses && user.guideOfClasses.length > 0;

    if (isMember || isTeacher || isGuide) {
      modalService.show(ModalVariant.MANAGEMENT_CHANGE_ROLE, {
        firstName: user.firstName,
        lastName: user.lastName ?? '',
        newRole,
        currentRole: (user.schoolRoles?.[0]?.role as SchoolUserRole) ?? SchoolUserRole.STUDENT,
        memberOfClasses: user.memberOfClasses,
        teacherOfClasses: user.teacherOfClasses,
        guideOfClasses: user.guideOfClasses,
        onConfirm: async () => {
          try {
            await changeUserRoleMutation.mutateAsync({
              userId,
              role: newRole,
              confirmChange: true,
            });
            toast.show(toastContent.management[ManagementStatusType.ROLE_CHANGE_SUCCESS]);
          } catch (error) {
            toast.show(toastContent.management[ManagementStatusType.ROLE_CHANGE_ERROR]);
          }
        },
      });
      return;
    }
    try {
      await changeUserRoleMutation.mutateAsync({
        userId,
        role: newRole,
      });
      toast.show(toastContent.management[ManagementStatusType.ROLE_CHANGE_SUCCESS]);
    } catch (error) {
      toast.show(toastContent.management[ManagementStatusType.ROLE_CHANGE_ERROR]);
    }
  };
  const usersColumns = getUsersColumns(handleRoleChange);

  const mappedUsers: UserData[] | undefined = usersData?.data?.map((mappingUser) => ({
    ...mappingUser,
    actionItem: (
      <NovaIconButton
        aria-label="löschen"
        variant="primary"
        size="s"
        padding="0"
        margin="0"
        color="icon.tertiary"
        onClick={() => handleKickUser(mappingUser.id, mappingUser.voucherId)}
        icon={DeleteIcon}
        iconProps={{ fill: 'transparent' }}
      />
    ),
  }));

  const handleInviteClick = (): void => {
    if (!selectedSchool) {
      toast.show(toastContent.management[ManagementStatusType.SELECT_SCHOOL]);
      return;
    }

    const schoolVouchers = allVouchersData?.data
      ?.map((voucher) => ({
        ...voucher,
        validUntil: voucher.validUntil || new Date(0),
        status: String(voucher.status),
        type: String(voucher.type),
      }))
      .filter((voucher) => voucher.status !== 'EXPIRED');

    if (!schoolVouchers || schoolVouchers.length === 0) {
      toast.show(toastContent.management[ManagementStatusType.NO_VOUCHER_FOUND]);
      return;
    }

    modalService.show(ModalVariant.ADMIN_INVITE, {
      selectedVoucherId: selectedVoucher,
      allVouchers: schoolVouchers,
    });
  };

  const handleSort = (sortBy: string, sortOrder: string): void => {
    navigateFilters(selectedVoucher, searchQuery, sortBy, sortOrder);
  };

  const clearInput = (): void => {
    setsearchQuery('');
    navigateFilters(selectedVoucher, '', sortBy, sortOrder);
    resetPagination();
  };

  if (isError) {
    return <QueryErrorState error={error} onRetry={() => refetch()} />;
  }

  return (
    <OrganizationManagementLayout
      pageTitle="Nutzerverwaltung"
      tabTitle="Nutzerverwaltung"
      handleInviteClick={handleInviteClick}
    >
      <VStack align="left" bg="background.primary" w="full" gap="l" borderRadius={{ md: '2xl' }}>
        <Filters
          filters={[
            [
              {
                id: 'voucher',
                placeholder: 'Alle Lizenzen',
                value: selectedVoucher,
                element: VoucherOption,
                options:
                  allVouchersData?.data?.map(
                    ({ id, shortCode, validUntil, timesUsed, allowedUsage }) => ({
                      id,
                      label: shortCode,
                      date: formattedDateWithMonthName(validUntil),
                      allowedUsage,
                      timesUsed,
                    })
                  ) || [],
                disabled: !selectedSchool || allVouchersData?.data.length === 0,
                onChange: handleVoucherChange,
                showPlaceholderAsFirstOption: true,
              },
            ],
            [
              {
                id: 'searchQuery',
                element: InputOption,
                placeholder: 'Nachname, Vorname oder E-Mail-Adresse',
                value: searchQuery,
                options: [],
                disabled: !selectedSchool || allVouchersData?.data.length === 0,
                onChange: handleSearchQueryChange,
                icon: <NovaIcon icon={SearchIcon} size="m" aria-hidden />,
                onClear: clearInput,
              },
            ],
          ]}
        />

        <Text textStyle="body.s.regular" color="text.quaternary">
          {usedBy ?? 0} Nutzerkonten
        </Text>

        <Table
          columns={usersColumns}
          data={mappedUsers || []}
          isDataLoading={isLoading}
          onSort={handleSort}
          sortBy={sortBy ?? undefined}
          sortDirection={sortOrder ?? undefined}
          scrollable
        />

        {!!(usersData?.totalPages && usersData?.totalPages > 1) && (
          <Pagination
            totalPages={usersData.totalPages}
            currentPage={currentPage}
            onPageChange={handlePageChange}
          />
        )}
      </VStack>
    </OrganizationManagementLayout>
  );
};
